import { Controller } from "@hotwired/stimulus"

// Power-K style palette: Ctrl/Cmd+K opens, fetches grouped results as HTML.
export default class extends Controller {
  static targets = ["dialog", "input", "results"]
  static values = { url: String }

  connect() {
    this.keydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        this.open()
      }
    }
    document.addEventListener("keydown", this.keydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydown)
    clearTimeout(this.debounce)
    this.abort?.abort()
  }

  open() {
    if (!this.hasDialogTarget) return
    this.dialogTarget.showModal()
    this.inputTarget.value = ""
    this.inputTarget.focus()
    // FD-120: populate the navigate/create "Actions" immediately (empty query).
    this.search()
  }

  async search() {
    const q = this.inputTarget.value.trim()
    // FD-120: fetch even for an empty/1-char query so the palette shows Actions;
    // the server only runs entity search at >= 2 chars.
    if (!this.urlValue) return
    clearTimeout(this.debounce)
    this.debounce = setTimeout(async () => {
      // Monotonic token: a slow earlier query must not clobber a newer one's results
      // (which would also let Enter open the wrong item). Re-check it after BOTH awaits.
      const seq = this.seq = (this.seq || 0) + 1
      // FD-485: abort the in-flight request too — the seq token only discards a
      // stale response client-side; the server would still pay for the render.
      this.abort?.abort()
      this.abort = new AbortController()
      try {
        const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(q)}&palette=1`, {
          headers: { Accept: "text/html" },
          signal: this.abort.signal
        })
        if (seq !== this.seq || !response.ok) return
        const html = await response.text()
        if (seq !== this.seq) return
        const doc = new DOMParser().parseFromString(html, "text/html")
        const fragment = doc.querySelector("#palette-results")
        if (fragment) this.resultsTarget.innerHTML = fragment.innerHTML
      } catch {
        // Aborted by a newer keystroke (or network failure) — the newer request
        // owns the results pane; render nothing.
      }
    }, 180)
  }

  navigate(event) {
    const links = Array.from(this.resultsTarget.querySelectorAll("a"))
    if (!links.length) return
    const focused = document.activeElement
    const index = links.indexOf(focused)
    if (event.key === "ArrowDown") {
      event.preventDefault()
      links[Math.min(index + 1, links.length - 1)].focus()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      if (index <= 0) this.inputTarget.focus()
      else links[index - 1].focus()
    } else if (event.key === "Enter" && index === -1 && links[0]) {
      event.preventDefault()
      links[0].click()
    }
  }
}
